import { useEffect, useMemo, useState } from "react";
import Logo from "../components/Logo";
import { useTheme } from "../hooks/useAppShell";
import { Sun, Moon, ExternalLink, Music, Share2, Calendar, Search, X, ChevronDown, ChevronRight, Folder } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Streaming platform badge builder — icon-labels shown next to each song
const PLATFORMS = [
  { key: "spotify",       label: "Spotify",       color: "#1DB954" },
  { key: "apple_music",   label: "Apple Music",   color: "#FA243C" },
  { key: "youtube_music", label: "YouTube Music", color: "#FF0033" },
  { key: "youtube_video", label: "YouTube",       color: "#FF0000" },
];

export default function PublicPitch() {
  const { theme, toggle: toggleTheme } = useTheme();
  const [data, setData] = useState({ songs: [], demos: [] });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [tab, setTab] = useState("released");
  const [copied, setCopied] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/public/pitch`);
        if (!res.ok) throw new Error(`Load failed (${res.status})`);
        setData(await res.json());
      } catch (e) { setErr(String(e.message || e)); }
      setLoading(false);
    })();
  }, []);

  const shareLink = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({ title: "YE Music — Pitch", url }).catch(() => {});
    } else {
      navigator.clipboard.writeText(url).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1600);
      }).catch(() => {});
    }
  };

  return (
    <div className="grain" style={{ minHeight: "100vh", padding: "40px 20px", position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", top: "-15%", left: "-8%", width: 600, height: 600, background: "radial-gradient(circle, rgba(124,108,255,0.24), transparent 70%)", filter: "blur(80px)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", bottom: "-15%", right: "-8%", width: 600, height: 600, background: "radial-gradient(circle, rgba(76,232,206,0.16), transparent 70%)", filter: "blur(80px)", pointerEvents: "none" }} />

      <div style={{ maxWidth: 960, margin: "0 auto", position: "relative", zIndex: 2 }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28, flexWrap: "wrap", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <Logo size={72} />
            <div>
              <div className="muted" style={{ fontSize: 10, letterSpacing: "0.22em", fontWeight: 600 }}>YE MUSIC</div>
              <div className="muted" style={{ fontSize: 9, letterSpacing: "0.18em", opacity: 0.7 }}>OFFICIAL PITCH</div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={shareLink}
              data-testid="share-pitch-btn"
              className="btn"
              style={{ padding: "8px 14px", borderRadius: 999, fontSize: 12, display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              <Share2 size={12} /> {copied ? "Link copied!" : "Share"}
            </button>
            <button
              onClick={toggleTheme}
              data-testid="theme-toggle-public"
              aria-label="Toggle theme"
              className="btn"
              style={{ padding: "8px 12px", borderRadius: 999 }}
            >
              {theme === "dark" ? <Sun size={14} /> : <Moon size={14} />}
            </button>
          </div>
        </div>

        {/* Hero */}
        <div style={{ marginBottom: 30 }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "5px 12px", borderRadius: 999, background: "rgba(124,108,255,0.10)", border: "1px solid rgba(124,108,255,0.28)", marginBottom: 14, fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 600, color: "#c4b8ff" }}>
            <Music size={12} /> Public Showcase
          </div>
          <h1 style={{ fontSize: 34, marginBottom: 10, lineHeight: 1.1, maxWidth: 640 }}>Our catalogue &amp; what's next</h1>
          <p className="muted" style={{ fontSize: 14, lineHeight: 1.65, maxWidth: 640 }}>
            Everything released under YE Music, plus the demos we're excited about. Feel free to share this link — anyone can view it, no login needed.
          </p>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 6, marginBottom: 22, borderBottom: "1px solid var(--border)" }}>
          {[
            { key: "released", label: `Released Songs · ${data.songs.length}` },
            { key: "demos", label: `Approved Demos · ${data.demos.length}` },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`tab-${t.key}`}
              style={{
                padding: "10px 16px", background: "transparent", border: "none",
                cursor: "pointer", color: tab === t.key ? "var(--text)" : "var(--muted)",
                borderBottom: tab === t.key ? "2px solid var(--violet)" : "2px solid transparent",
                fontSize: 13, fontWeight: 600, letterSpacing: "0.02em",
                transition: "color 200ms",
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Master search */}
        <div style={{ marginBottom: 18, position: "relative" }}>
          <Search size={14} style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: "var(--muted)", pointerEvents: "none" }} />
          <input
            className="input"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="pitch-search"
            placeholder={tab === "released" ? "Search songs or artists…" : "Search demos, producers or genres…"}
            style={{ paddingLeft: 38, paddingRight: query ? 40 : 14, borderRadius: 999, height: 40 }}
          />
          {query && (
            <button
              onClick={() => setQuery("")}
              data-testid="pitch-search-clear"
              aria-label="Clear search"
              className="btn"
              style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", padding: "4px 6px", borderRadius: 999 }}
            >
              <X size={12} />
            </button>
          )}
        </div>

        {/* Content */}
        {loading ? (
          <div className="panel muted" style={{ padding: 40, textAlign: "center" }}>Loading catalogue…</div>
        ) : err ? (
          <div className="panel" data-testid="pitch-error" style={{ padding: 24, color: "var(--danger)", fontSize: 13 }}>{err}</div>
        ) : tab === "released" ? (
          <SongsGrid songs={data.songs} query={query} />
        ) : (
          <DemosGrid demos={data.demos} query={query} />
        )}

        <div className="muted" style={{ textAlign: "center", marginTop: 40, fontSize: 11, letterSpacing: "0.14em", lineHeight: 1.7 }}>
          © YE MUSIC · BUILT FOR ARTISTS. BACKED BY VISION.<br />
          <span style={{ opacity: 0.6 }}>Interested in signing? Reach out via our team channels.</span>
        </div>
      </div>
    </div>
  );
}

function SongsGrid({ songs, query }) {
  // Each song belongs to exactly one folder — the roster artist the label
  // has toggled "Show on Pitch" for. Backend has already resolved this into
  // `s.pitch_artist`, so we just trust it and group by that value.
  const grouped = useMemo(() => {
    const q = (query || "").trim().toLowerCase();
    const filtered = q
      ? songs.filter(s => {
          const hay = [s.title, ...(s.artists || [])].join(" ").toLowerCase();
          return hay.includes(q);
        })
      : songs;
    const byArtist = new Map();
    filtered.forEach(s => {
      const key = s.pitch_artist || "Untagged";
      if (!byArtist.has(key)) byArtist.set(key, []);
      byArtist.get(key).push(s);
    });
    // Sort artists alphabetically, each folder sorted by release date desc.
    const entries = Array.from(byArtist.entries()).sort(([a], [b]) => a.localeCompare(b));
    entries.forEach(([, list]) => list.sort((x, y) => (y.release_date || "").localeCompare(x.release_date || "")));
    return { entries, total: filtered.length };
  }, [songs, query]);

  const [openArtists, setOpenArtists] = useState(() => new Set());
  // If the user is searching, auto-expand every matching folder so results are visible.
  const isSearching = (query || "").trim().length > 0;
  const isOpen = (name) => isSearching || openArtists.has(name);
  const toggle = (name) => {
    setOpenArtists(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  };

  if (songs.length === 0) {
    return <div className="panel muted" style={{ padding: 40, textAlign: "center" }} data-testid="no-songs">No releases yet — release a song and toggle its artist's <b>Show on Public Pitch</b> switch in Artist Database to feature it here.</div>;
  }
  if (grouped.total === 0) {
    return <div className="panel muted" style={{ padding: 40, textAlign: "center" }} data-testid="no-songs-match">No songs match "{query}". Try a different artist or title.</div>;
  }

  return (
    <div data-testid="songs-grouped" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {grouped.entries.map(([artist, list]) => {
        const open = isOpen(artist);
        return (
          <div key={artist} className="panel" style={{ padding: 0, overflow: "hidden" }} data-testid={`artist-folder-${artist}`}>
            <button
              onClick={() => toggle(artist)}
              data-testid={`artist-toggle-${artist}`}
              className="clickable"
              style={{
                width: "100%", display: "flex", alignItems: "center", gap: 12,
                padding: "16px 18px", background: "transparent", border: "none",
                cursor: "pointer", textAlign: "left", color: "var(--text)",
              }}
            >
              {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              <Folder size={16} style={{ color: "var(--mint)" }} />
              <span style={{ fontSize: 15, fontWeight: 700, flex: 1 }}>{artist}</span>
              <span className="badge" style={{ fontSize: 11, padding: "3px 10px" }}>
                {list.length} {list.length === 1 ? "song" : "songs"}
              </span>
            </button>
            {open && (
              <div style={{ padding: "6px 18px 18px", display: "flex", flexWrap: "wrap", gap: 14 }}>
                {list.map(s => (
                  <div key={s.id} data-testid={`song-${s.id}`} style={{ padding: 14, background: "var(--panel2)", border: "1px solid var(--border)", borderRadius: 10, display: "flex", gap: 12, flex: "1 1 280px", maxWidth: 380 }}>
                    {s.cover_art_url ? (
                      <img
                        src={`${API}${s.cover_art_url}`}
                        alt=""
                        loading="lazy"
                        data-testid={`song-cover-${s.id}`}
                        style={{ width: 72, height: 72, borderRadius: 8, objectFit: "cover", flexShrink: 0 }}
                      />
                    ) : (
                      <div
                        aria-hidden
                        data-testid={`song-cover-placeholder-${s.id}`}
                        style={{
                          width: 72, height: 72, borderRadius: 8, flexShrink: 0,
                          background: "linear-gradient(135deg, rgba(124,108,255,0.35), rgba(76,232,206,0.25))",
                          border: "1px solid var(--border)",
                          display: "flex", alignItems: "center", justifyContent: "center",
                        }}
                      >
                        <Music size={22} style={{ color: "rgba(255,255,255,0.6)" }} />
                      </div>
                    )}
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4, lineHeight: 1.3 }}>{s.title}</div>
                      {s.artists && s.artists.length > 1 && (
                        <div className="muted" style={{ fontSize: 11.5, marginBottom: 8, lineHeight: 1.5 }}>
                          with {s.artists.filter(a => a !== artist).join(", ") || s.artists.join(", ")}
                        </div>
                      )}
                      {s.release_date && (
                        <div className="muted" style={{ fontSize: 11, marginBottom: 10, display: "inline-flex", alignItems: "center", gap: 4 }}>
                          <Calendar size={11} /> {formatDate(s.release_date)}
                        </div>
                      )}
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                        {PLATFORMS.filter(p => s[p.key]).map(p => (
                          <a
                            key={p.key}
                            href={s[p.key]}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`link-${p.key}-${s.id}`}
                            className="clickable"
                            style={{
                              fontSize: 11, padding: "5px 10px", borderRadius: 999,
                              background: "var(--panel)", border: "1px solid var(--border)",
                              color: p.color, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 4,
                              textDecoration: "none",
                            }}
                          >
                            {p.label} <ExternalLink size={10} />
                          </a>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function DemosGrid({ demos, query }) {
  const filtered = useMemo(() => {
    const q = (query || "").trim().toLowerCase();
    if (!q) return demos;
    return demos.filter(d => {
      const hay = [d.song, d.producer, d.genre].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [demos, query]);

  if (demos.length === 0) {
    return <div className="panel muted" style={{ padding: 40, textAlign: "center" }} data-testid="no-demos">No approved demos yet — approve a demo in Demo Submissions to feature it here.</div>;
  }
  if (filtered.length === 0) {
    return <div className="panel muted" style={{ padding: 40, textAlign: "center" }} data-testid="no-demos-match">No demos match "{query}". Try a different producer, genre or title.</div>;
  }
  return (
    <div data-testid="demos-grid" style={{ display: "flex", flexWrap: "wrap", gap: 14 }}>
      {filtered.map(d => (
        <div key={d.id} data-testid={`demo-${d.id}`} className="panel" style={{ padding: 18, flex: "1 1 280px", maxWidth: 380 }}>
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4, lineHeight: 1.3 }}>{d.song}</div>
          {d.producer && (
            <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>by {d.producer}</div>
          )}
          {d.genre && (
            <span className="badge" style={{ fontSize: 10, padding: "3px 8px", marginBottom: 10, display: "inline-block" }}>{d.genre}</span>
          )}
          {d.status && (
            <span
              data-testid={`demo-status-${d.id}`}
              className={`badge ${d.status === "Approved" ? "badge-mint" : ""}`}
              style={{ fontSize: 10, padding: "3px 8px", marginLeft: 6 }}
            >
              {d.status}
            </span>
          )}
          {d.soundcloud && (
            <div style={{ marginTop: 12 }}>
              <a
                href={d.soundcloud}
                target="_blank"
                rel="noreferrer"
                data-testid={`demo-link-${d.id}`}
                className="clickable"
                style={{
                  fontSize: 11, padding: "5px 10px", borderRadius: 999,
                  background: "var(--panel2)", border: "1px solid var(--border)",
                  color: "var(--indigo)", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 4,
                  textDecoration: "none",
                }}
              >
                Listen <ExternalLink size={10} />
              </a>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function formatDate(d) {
  if (!d) return "";
  try {
    return new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch { return String(d); }
}
